import React, { useState, useEffect, useMemo } from 'react';
import DOMPurify from 'dompurify';
import { previewBCP } from '../../services/api';
import { getAccessToken } from '../../utils/authToken';
import { useSubscriptionContext } from '../../contexts/SubscriptionContext';
import { useAuth } from '../../contexts/AuthContext';
import SubscriptionUpgradeModal from '../SubscriptionUpgradeModal';

interface Template {
  id: string;
  name: string;
  description: string;
  features: string[];
  recommended_for: string[];
  file_name: string;
}

interface DocumentPreviewProps {
  isOpen: boolean;
  onClose: () => void;
  bcpId: number;
  bcpName: string;
  isAdmin?: boolean;
}

const DocumentPreview: React.FC<DocumentPreviewProps> = ({
  isOpen,
  onClose,
  bcpId,
  bcpName,
  isAdmin = false
}) => {
  const [htmlContent, setHtmlContent] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeFeature, setUpgradeFeature] = useState<{ feature: 'essential' | 'gold' | 'premium', name: string } | null>(null);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<string>('standard');
  const [isUpdatingTemplate, setIsUpdatingTemplate] = useState(false);

  const { hasActiveSubscription, canAccessFeature } = useSubscriptionContext();
  const { user } = useAuth();
  const userIsAdmin = isAdmin || !!(user && (user.is_superuser || user.role === 'ADMIN' || user.role === 'SUPER_ADMIN'));

  useEffect(() => {
    if (isOpen && bcpId) {
      loadPreview();
      fetchTemplates();
    }
  }, [isOpen, bcpId]);

  useEffect(() => {
    if (isOpen && bcpId && selectedTemplate) {
      loadPreview();
    }
  }, [selectedTemplate]);

  const fetchTemplates = async () => {
    try {
      const token = getAccessToken();
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      const response = await fetch(`${API_BASE_URL}/v1/bcp/templates`, {
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setTemplates(data.templates);
      } else {
        console.error('Failed to fetch templates:', response.status, response.statusText);
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  const updateBcpTemplate = async (templateId: string) => {
    try {
      setIsUpdatingTemplate(true);
      const token = getAccessToken();
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      const endpoint = userIsAdmin
        ? `${API_BASE_URL}/v1/admin/bcps/${bcpId}/template`
        : `${API_BASE_URL}/v1/bcp/${bcpId}/template`;

      const response = await fetch(endpoint, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ template_type: templateId }),
      });

      if (!response.ok) {
        console.error('Failed to update template');
      }
    } catch (error) {
      console.error('Error updating template:', error);
    } finally {
      setIsUpdatingTemplate(false);
    }
  };

  const loadPreview = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Update the BCP template before loading preview if template has changed
      if (selectedTemplate) {
        await updateBcpTemplate(selectedTemplate);
      }

      const content = await previewBCP(bcpId, userIsAdmin);
      setHtmlContent(content);
    } catch (err) {
      console.error('Failed to load preview:', err);
      setError('Failed to load document preview');
    } finally {
      setIsLoading(false);
    }
  };

  const handleTemplateChange = (templateId: string) => {
    setSelectedTemplate(templateId);
  };

  const canPrintDownload = userIsAdmin || hasActiveSubscription();

  const sanitizedHtml = useMemo(
    () => DOMPurify.sanitize(htmlContent, { USE_PROFILES: { html: true } }),
    [htmlContent]
  );

  const handlePrint = () => {
    if (!canPrintDownload) {
      setUpgradeFeature({ feature: 'essential', name: 'Print Documents' });
      setShowUpgradeModal(true);
      return;
    }

    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(sanitizedHtml);
      printWindow.document.close();
      printWindow.print();
    }
  };

  const handleDownload = () => {
    if (!canPrintDownload) {
      setUpgradeFeature({ feature: 'essential', name: 'Download Documents' });
      setShowUpgradeModal(true);
      return;
    }

    const blob = new Blob([htmlContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `bcp_${bcpName}_preview.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
          onClick={onClose}
        />

        {/* Modal */}
        <div className="relative w-full max-w-6xl bg-white rounded-2xl shadow-2xl">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div className="flex items-center space-x-3">
              <div className="flex justify-center items-center w-10 h-10 bg-gradient-to-r from-blue-600 to-green-600 rounded-xl">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Document Preview</h2>
                <p className="text-sm text-gray-600">{bcpName}</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              {/* Template Selector */}
              {templates.length > 0 && (
                <div className="flex items-center space-x-2">
                  <label className="text-sm font-medium text-gray-700">Template:</label>
                  <select
                    value={selectedTemplate}
                    onChange={(e) => handleTemplateChange(e.target.value)}
                    className="px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    disabled={isLoading || isUpdatingTemplate}
                  >
                    {templates.map((template) => (
                      <option key={template.id} value={template.id}>
                        {template.name}
                      </option>
                    ))}
                  </select>
                  {isUpdatingTemplate && (
                    <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                  )}
                </div>
              )}

              {/* Action Buttons */}
              <button
                onClick={handlePrint}
                className={`px-3 py-2 text-sm transition-colors rounded-md border ${
                  canPrintDownload
                    ? 'text-blue-600 bg-blue-50 hover:bg-blue-100 border-blue-200'
                    : 'text-gray-500 bg-gray-100 border-gray-200 cursor-not-allowed'
                }`}
                title={canPrintDownload ? "Print Document" : "Upgrade required to print"}
                disabled={!canPrintDownload}
              >
                <svg className="w-4 h-4 mr-2 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                </svg>
                {canPrintDownload ? 'Print' : 'Print (Upgrade)'}
              </button>
              <button
                onClick={handleDownload}
                className={`px-3 py-2 text-sm transition-colors rounded-md border ${
                  canPrintDownload
                    ? 'text-green-600 bg-green-50 hover:bg-green-100 border-green-200'
                    : 'text-gray-500 bg-gray-100 border-gray-200 cursor-not-allowed'
                }`}
                title={canPrintDownload ? "Download HTML" : "Upgrade required to download"}
                disabled={!canPrintDownload}
              >
                <svg className="w-4 h-4 mr-2 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                {canPrintDownload ? 'Download' : 'Download (Upgrade)'}
              </button>
              <button
                onClick={onClose}
                className="p-2 text-gray-400 hover:text-gray-600 transition-colors rounded-lg hover:bg-gray-100"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 max-h-[80vh] overflow-y-auto">
            {isLoading ? (
              <div className="flex flex-col items-center justify-center py-12">
                <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-4"></div>
                <p className="text-lg text-gray-600">Loading document preview...</p>
              </div>
            ) : error ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 mx-auto mb-4 text-red-500">
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <p className="text-lg text-gray-600 mb-2">Failed to load preview</p>
                <p className="text-sm text-gray-500 mb-4">{error}</p>
                <button
                  onClick={loadPreview}
                  className="px-4 py-2 text-sm text-blue-600 bg-blue-50 hover:bg-blue-100 transition-colors rounded-md border border-blue-200"
                >
                  Try Again
                </button>
              </div>
            ) : (
              <div
                className="document-preview bg-white border border-gray-200 rounded-lg p-8"
                dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
              />
            )}
          </div>
        </div>
      </div>

      {/* Subscription Upgrade Modal */}
      {showUpgradeModal && upgradeFeature && (
        <SubscriptionUpgradeModal
          isOpen={showUpgradeModal}
          onClose={() => {
            setShowUpgradeModal(false);
            setUpgradeFeature(null);
          }}
          requiredFeature={upgradeFeature.feature}
          featureName={upgradeFeature.name}
        />
      )}
    </div>
  );
};

export default DocumentPreview;
